import * as Sentry from "@sentry/react";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import { isNetworkError } from "@/offline/network";

function httpStatus(err: unknown): number | undefined {
  if (!err || typeof err !== "object") return undefined;
  return (err as { response?: { status?: number } }).response?.status;
}

/**
 * Decides whether a failed page load is worth a Sentry event.
 *
 * Network errors are the user being offline, and 401/403 are already handled
 * (the client interceptor redirects to login; pages show an access message),
 * so none of those are actionable.
 */
export function shouldReportQueryError(err: unknown): boolean {
  if (isNetworkError(err)) return false;
  const status = httpStatus(err);
  return status !== 401 && status !== 403;
}

/**
 * Retries a server error once; anything else fails straight away.
 *
 * A network error or 4xx won't fix itself in a few seconds, so retrying only
 * delays the error state the user sees.
 */
export function shouldRetryQuery(failureCount: number, err: unknown): boolean {
  const status = httpStatus(err);
  return status !== undefined && status >= 500 && failureCount < 1;
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      if (shouldReportQueryError(err)) Sentry.captureException(err);
    },
  }),
  defaultOptions: {
    queries: {
      // The default ("online") pauses queries while offline, which leaves a
      // page spinning forever. Fail instead so the page can say it's offline.
      networkMode: "always",
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
    },
  },
});
